// SAISIE — écran unique et partagé : toutes les saisies du mois au même endroit (onglets), une donnée saisie UNE seule fois.
// Les onglets restent montés à la demande ; l'onglet choisi est mémorisé (reprise après coupure / rechargement).
import { useState } from 'react';
import type { ReactNode } from 'react';
import SaisieUnique from './SaisieUnique.tsx';
import SaisieCpn from './SaisieCpn.tsx';
import Pev from './modules/Pev.tsx';
import Intrants from './modules/Intrants.tsx';
import Finance from './modules/Finance.tsx';
import Deces from './modules/Deces.tsx';
import Communication from './modules/Communication.tsx';
import { Onglets, PasDePeriode, nomMois } from '../components/ui.tsx';
import { useModules } from '../hooks/useModules.ts';
import { useAbonnement } from '../hooks/useAbonnement.tsx';
import Verrou, { VerrouAdmin } from '../components/Verrou.tsx';
import { moduleAutorise } from '../config/permissions.ts';
import type { ModuleAbonnement } from '../../../shared/business-logic/abonnement.ts';
import type { Periode } from './Accueil.tsx';

type Id = 'RCE' | 'CPN' | 'PEV' | 'INTRANTS' | 'FINANCE' | 'DECES' | 'COM';
const CLE = 'prerma_onglet_saisie';
const ONGLETS: Array<{ id: Id; label: string; couleur: string; icone: string; module: ModuleAbonnement; progression?: string }> = [
  { id: 'RCE', module: 'CONSULTATION', label: 'Consultations', couleur: '#1565c0', icone: 'cons' },
  { id: 'CPN', module: 'CPN', label: 'CPN', couleur: '#b4237a', icone: 'cpn' },
  { id: 'PEV', module: 'PEV', label: 'PEV', couleur: '#0a6e8a', icone: 'pev', progression: 'PEV' },
  { id: 'INTRANTS', module: 'INTRANTS', label: 'Intrants', couleur: '#c24a00', icone: 'stk', progression: 'STOCK' },
  { id: 'FINANCE', module: 'FINANCE', label: 'Finance', couleur: '#0a5ea8', icone: 'fin', progression: 'FINANCE' },
  { id: 'DECES', module: 'DECES', label: 'Décès', couleur: '#5d3fb0', icone: 'dec', progression: 'CAUSES_DECES' },
  { id: 'COM', module: 'COMMUNICATION', label: 'Communication', couleur: '#455a64', icone: 'com', progression: 'COMMUNICATION' },
];

export default function Saisie({ periode, onAbonnement }: { periode: Periode | null; onAbonnement?: () => void }) {
  const { acces } = useAbonnement();
  const [actif, setActif] = useState<Id>(() => (sessionStorage.getItem(CLE) as Id) || 'RCE');
  const m = useModules(periode);
  if (!periode) return <PasDePeriode />;
  const prog: Record<string, boolean> = (m.calc?.progression as any) ?? {};
  const choisir = (id: Id) => { sessionStorage.setItem(CLE, id); setActif(id); };
  const vues: Record<Id, ReactNode> = {
    RCE: <SaisieUnique periode={periode} />, CPN: <SaisieCpn periode={periode} />, PEV: <Pev periode={periode} />,
    INTRANTS: <Intrants periode={periode} />, FINANCE: <Finance periode={periode} />, DECES: <Deces periode={periode} />, COM: <Communication periode={periode} />,
  };
  return (
    <div>
      <div className="bandeau-periode">Saisie de <b>{nomMois(periode.mois)} {periode.annee}</b>{periode.statut === 'CLOTURE' && ' · 🔒 clôturé'}</div>
      <Onglets actif={actif} onChoix={choisir}
        onglets={ONGLETS.map((o) => ({ id: o.id, label: `${!acces.modules[o.module] || !moduleAutorise(o.module) ? '🔒 ' : ''}${o.progression && prog[o.progression] ? '✓ ' : ''}${o.label}`, couleur: o.couleur, icone: o.icone }))} />
      {!moduleAutorise(ONGLETS.find((o) => o.id === actif)!.module) ? <VerrouAdmin module={ONGLETS.find((o) => o.id === actif)!.module} /> : acces.modules[ONGLETS.find((o) => o.id === actif)!.module] ? vues[actif] : <Verrou module={ONGLETS.find((o) => o.id === actif)!.module} onAbonnement={() => onAbonnement?.()} />}
    </div>
  );
}
